import { useState } from "react";
import type { ReactNode } from "react";
import "./GitConn.css";
import { Button } from "./Button";
import { Alert } from "./Card";
import { Field, Input } from "./Field";
import { Chip } from "./Status";
import { authErrText } from "../lib/auth";
import { connState, gitConnApi } from "../lib/gitconn";
import type { GitConn, GitConnList, GitSetup } from "../lib/gitconn";
import { tx } from "../i18n";

/* ══ git 호스트 연결 표 — 채택 시안 Figma 22:3 ══
   한 줄 = 연결 하나. 상태는 모양+글자(● 연결됨 · ◌ 승인 전 · ! 다시 연결). 「연결 추가」는 표 위에서 펼친다 —
   GitHub 은 링크 한 번(앱 만들기 → 설치 → 승인, `git_app.rs`), 다른 호스트는 앱 등록 한 번 + 승인 링크.
   토큰은 이 화면에 한 번도 안 나온다. 옛 설치 자격(gitea.json)은 맨 아래 줄 「설치 기본」이다. */

const HOST_NAME: Record<string, string> = { github: "GitHub", gitlab: "GitLab", gitea: "Gitea", bitbucket: "Bitbucket" };
const hostName = (p: string) => HOST_NAME[p] ?? p;
/** 주소가 고정인 클라우드 호스트 — 주소 칸을 안 묻는다. */
const CLOUD = new Set(["github", "bitbucket"]);

export type Legacy = { baseUrl: string; tokenSet: boolean; repoCount: number | null };

async function goAuthorize(id: string) {
  const r = await gitConnApi.authorize(id);
  location.href = r.url;
}

export function GitConnSection({
  data,
  legacy,
  onLegacyFix,
  legacyForm,
  back,
  onChanged,
}: {
  data: GitConnList | null;
  legacy: Legacy | null;
  onLegacyFix: () => void;
  /** 설치 기본 줄의 「고치기」를 눌렀을 때 표 아래에 펼칠 옛 폼. */
  legacyForm?: ReactNode;
  /** 승인에서 돌아온 결과(`?connected=` · `?git_error=`). */
  back?: { connected?: string | null; error?: string | null };
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);
  const conns = data?.connections ?? [];
  const ok = conns.filter((c) => connState(c) === "ok").length;

  async function connect(c: GitConn) {
    setBusy(c.id);
    setRowErr(null);
    try {
      await goAuthorize(c.id);
    } catch (e) {
      setRowErr(authErrText(e));
      setBusy(null);
    }
  }

  async function remove(c: GitConn) {
    if (c.repoCount > 0) {
      setRowErr(tx("gitConn.removeBlocked", { n: c.repoCount }));
      return;
    }
    if (armed !== c.id) {
      setArmed(c.id);
      return;
    }
    setBusy(c.id);
    setRowErr(null);
    try {
      await gitConnApi.remove(c.id);
      setArmed(null);
      onChanged();
    } catch (e) {
      setRowErr(authErrText(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="gconn" aria-label={tx("gitConn.table")}>
      {back?.connected ? <Alert tone="success">{tx("gitConn.back.connected")}</Alert> : null}
      {back?.error ? <Alert tone="error">{tx("gitConn.back.error", { kind: back.error })}</Alert> : null}
      <div className="gconn__bar">
        <b>{tx("gitConn.title")}</b>
        <span className="muted">{tx("gitConn.summary", { n: conns.length, ok })}</span>
        <span className="git__spacer" />
        {!adding ? (
          <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
            {tx("gitConn.add")}
          </Button>
        ) : null}
      </div>
      {adding ? <AddPanel providers={data?.providers ?? []} onClose={() => setAdding(false)} /> : null}
      {rowErr ? <Alert tone="error">{rowErr}</Alert> : null}
      <div className="b-table-wrap">
        <table className="b-table gconn__table" aria-label={tx("gitConn.table")}>
          <thead>
            <tr>
              <th>{tx("gitConn.col.state")}</th>
              <th>{tx("gitConn.col.label")}</th>
              <th>{tx("gitConn.col.host")}</th>
              <th>{tx("gitConn.col.account")}</th>
              <th>{tx("gitConn.col.repos")}</th>
              <th>{tx("gitConn.col.memo")}</th>
              <th>{tx("gitConn.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {conns.map((c) => {
              const st = connState(c);
              return (
                <tr key={c.id} className={back?.connected === c.id ? "is-fresh" : undefined}>
                  <td className={`gconn__state gconn__state--${st}`}>{tx(`gitConn.state.${st}`)}</td>
                  <td className="is-primary">{c.label}</td>
                  <td>
                    <Chip>{c.provider}</Chip>
                  </td>
                  <td>{c.account ?? tx("gitConn.unknown")}</td>
                  <td className="tnum">{c.repoCount}</td>
                  <td className="muted">
                    {st === "broken" ? tx("gitConn.brokenMemo", { err: c.lastError ?? "" }) : st === "ok" && c.refreshSet ? tx("gitConn.autoRefresh") : ""}
                  </td>
                  <td className="gconn__acts">
                    {st !== "ok" ? (
                      <Button size="sm" variant="primary" loading={busy === c.id} onClick={() => connect(c)}>
                        {tx(st === "broken" ? "gitConn.reconnect" : "gitConn.connect")}
                      </Button>
                    ) : null}
                    <Button size="sm" variant={armed === c.id ? "danger" : "ghost"} disabled={busy === c.id} onClick={() => remove(c)}>
                      {tx(armed === c.id ? "gitConn.removeArmed" : "gitConn.remove")}
                    </Button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td className={`gconn__state gconn__state--${legacy?.baseUrl ? "ok" : "pending"}`}>{legacy?.baseUrl ? tx("gitConn.state.legacy") : tx("gitConn.state.none")}</td>
              <td className="is-primary">{tx("gitConn.legacyLabel")}</td>
              <td>
                <Chip>gitea</Chip>
              </td>
              <td>{tx("gitConn.unknown")}</td>
              <td className="tnum">{legacy?.repoCount ?? tx("gitConn.unknown")}</td>
              <td className="muted">{legacy?.baseUrl ? `${legacy.baseUrl} · ${tx("gitConn.legacyMemo")}` : tx("gitConn.legacyNone")}</td>
              <td className="gconn__acts">
                <Button size="sm" variant="ghost" onClick={onLegacyFix}>
                  {tx("gitConn.legacyFix")}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {legacyForm}
    </section>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="gconn__copy">
      <span className="gconn__copyk">{label}</span>
      <code className="gconn__copyv">{value}</code>
      <Button
        size="sm"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => setDone(true), () => setDone(false));
        }}
      >
        {tx(done ? "gitConn.manual.copied" : "gitConn.manual.copy")}
      </Button>
    </div>
  );
}

function AddPanel({ providers: given, onClose }: { providers: string[]; onClose: () => void }) {
  // 링크 한 번으로 끝나는 GitHub 을 맨 앞에 — 손이 덜 가는 길을 먼저 보인다.
  const providers = [...given].sort((a, b) => Number(b === "github") - Number(a === "github"));
  const [provider, setProvider] = useState(providers.includes("github") ? "github" : providers[0] ?? "gitea");
  const [label, setLabel] = useState("");
  const [org, setOrg] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [setup, setSetup] = useState<GitSetup | null>(null);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cloud = CLOUD.has(provider);

  function pick(p: string) {
    setProvider(p);
    setSetup(null);
    setErr(null);
  }

  /** 앱 등록 화면을 연다 — 같은 답에 거기 적을 콜백 주소 · 범위가 온다. */
  async function openSetup() {
    setErr(null);
    try {
      const s = await gitConnApi.setup(provider, baseUrl.trim());
      setSetup(s);
      window.open(s.appSetupUrl, "_blank", "noopener");
    } catch (e) {
      setErr(authErrText(e));
    }
  }

  async function createAndAuthorize() {
    setBusy(true);
    setErr(null);
    try {
      const c = await gitConnApi.create({
        provider,
        label: label.trim() || hostName(provider),
        baseUrl: baseUrl.trim(),
        clientId: clientId.trim(),
        clientSecret: secret,
      });
      await goAuthorize(c.id);
    } catch (e) {
      setErr(authErrText(e));
      setBusy(false);
    }
  }

  function githubLink() {
    const q = new URLSearchParams({ provider: "github" });
    if (label.trim()) q.set("label", label.trim());
    if (org.trim()) q.set("org", org.trim());
    // 서버가 매니페스트를 GitHub 으로 스스로 POST 하는 쪽지를 낸다 — 이 탭이 그대로 넘어간다.
    location.href = `${import.meta.env.VITE_B3XIN_API_URL ?? "/api"}/admin/git-connections/app/new?${q}`;
  }

  return (
    <div className="b-card gconn__add">
      <div className="gconn__addhead">
        <h3 className="b-card-title">{tx("gitConn.addTitle")}</h3>
        <span className="git__spacer" />
        <Button size="sm" variant="ghost" onClick={onClose}>
          {tx("gitConn.cancel")}
        </Button>
      </div>
      <div className="gconn__hosts" role="radiogroup">
        {providers.map((p) => (
          <Button key={p} size="sm" variant={p === provider ? "primary" : "secondary"} aria-pressed={p === provider} onClick={() => pick(p)}>
            {hostName(p)}
          </Button>
        ))}
      </div>
      {provider === "github" ? (
        <div className="gconn__pane">
          <b>{tx("gitConn.github.head")}</b>
          <p className="muted">{tx("gitConn.github.lead")}</p>
          <div className="gconn__row">
            <Field label={tx("gitConn.github.label")}>
              {(id) => <Input id={id} value={label} placeholder="GitHub" onChange={(e) => setLabel(e.target.value)} />}
            </Field>
            <Field label={tx("gitConn.github.org")}>
              {(id) => <Input id={id} value={org} placeholder="irolab-co" onChange={(e) => setOrg(e.target.value)} />}
            </Field>
          </div>
          <div>
            <Button variant="primary" onClick={githubLink}>
              {tx("gitConn.github.go")}
            </Button>
          </div>
          <p className="caption">{tx("gitConn.github.foot")}</p>
        </div>
      ) : (
        <div className="gconn__pane">
          <b>{tx("gitConn.manual.head", { host: hostName(provider) })}</b>
          <ol className="gconn__steps">
            <li>
              <span>{tx(cloud ? "gitConn.manual.step1Cloud" : "gitConn.manual.step1")}</span>
              <div className="gconn__row">
                {cloud ? null : (
                  <Field label={tx("gitConn.manual.baseUrl")}>
                    {(id) => <Input id={id} value={baseUrl} inputMode="url" placeholder="https://gitlab.example.com" onChange={(e) => setBaseUrl(e.target.value)} />}
                  </Field>
                )}
                <Button onClick={openSetup} disabled={!cloud && !baseUrl.trim()}>
                  {tx("gitConn.manual.open")}
                </Button>
              </div>
            </li>
            {setup ? (
              <li>
                <span>{tx("gitConn.manual.step2")}</span>
                <CopyRow label={tx("gitConn.manual.redirect")} value={setup.redirectUri} />
                {setup.scope ? <CopyRow label={tx("gitConn.manual.scope")} value={setup.scope} /> : <p className="caption">{tx("gitConn.manual.scopeNone")}</p>}
              </li>
            ) : null}
            {setup ? (
              <li>
                <span>{tx("gitConn.manual.step3")}</span>
                <div className="gconn__row">
                  <Field label={tx("gitConn.manual.label")}>
                    {(id) => <Input id={id} value={label} placeholder={hostName(provider)} onChange={(e) => setLabel(e.target.value)} />}
                  </Field>
                  <Field label={tx("gitConn.manual.clientId")}>
                    {(id) => <Input id={id} value={clientId} autoComplete="off" onChange={(e) => setClientId(e.target.value)} />}
                  </Field>
                  <Field label={tx("gitConn.manual.secret")}>
                    {(id) => <Input id={id} type="password" value={secret} autoComplete="off" onChange={(e) => setSecret(e.target.value)} />}
                  </Field>
                </div>
                <div>
                  <Button variant="primary" loading={busy} disabled={!clientId.trim() || !secret} onClick={createAndAuthorize}>
                    {tx("gitConn.manual.go")}
                  </Button>
                </div>
              </li>
            ) : null}
          </ol>
        </div>
      )}
      {err ? <Alert tone="error">{err}</Alert> : null}
    </div>
  );
}
